/**
 * Output Schemas API
 *
 * Provides:
 * - AI-assisted JSON Schema generation from example JSON
 * - CRUD operations for centralized output schema management
 */

import apiClient, { baseApiClient } from './client';
import type {
  OutputSchema,
  OutputSchemaCreate,
  OutputSchemaUpdate,
  OutputSchemaListResponse,
  OutputSchemaListItemsResponse,
  OutputSchemaListParams,
} from '@/types/outputSchema';

// ============================================================================
// AI Generation Types
// ============================================================================

export interface SchemaGenerateRequest {
  example_json: string;
  model: string;
  language?: string;
}

export interface SchemaGenerateResponse {
  json_schema: Record<string, unknown>;
  example_output: Record<string, unknown>;
  format_instruction: string;
  suggested_name: string;
  suggested_key: string;
  explanation: string;
}

// ============================================================================
// Admin API (CRUD operations)
// ============================================================================

export const outputSchemasAdminApi = {
  /**
   * List all output schemas with optional filters
   */
  list: async (params?: OutputSchemaListParams): Promise<OutputSchemaListResponse> => {
    const response = await apiClient.get('/output-schemas', { params });
    return response.data;
  },

  /**
   * Get a single output schema by key
   */
  get: async (key: string): Promise<OutputSchema> => {
    const response = await apiClient.get(`/output-schemas/${key}`);
    return response.data;
  },

  /**
   * Create a new output schema
   */
  create: async (data: OutputSchemaCreate): Promise<OutputSchema> => {
    const response = await apiClient.post('/output-schemas', data);
    return response.data;
  },

  /**
   * Update an existing output schema
   */
  update: async (key: string, data: OutputSchemaUpdate): Promise<OutputSchema> => {
    const response = await apiClient.put(`/output-schemas/${key}`, data);
    return response.data;
  },

  /**
   * Delete an output schema
   */
  delete: async (key: string): Promise<void> => {
    await apiClient.delete(`/output-schemas/${key}`);
  },

  /**
   * Toggle the enabled status of an output schema
   */
  toggle: async (key: string, enabled: boolean): Promise<OutputSchema> => {
    const response = await apiClient.patch(`/output-schemas/${key}/toggle`, null, {
      params: { enabled },
    });
    return response.data;
  },

  /**
   * Generate JSON Schema from example JSON using AI
   */
  generate: async (data: SchemaGenerateRequest): Promise<SchemaGenerateResponse> => {
    const response = await apiClient.post<SchemaGenerateResponse>(
      '/output-schemas/generate',
      data
    );
    return response.data;
  },
};

// ============================================================================
// Public API (Read-only for consumers)
// ============================================================================

export const outputSchemasApi = {
  /**
   * List all enabled output schemas
   */
  list: async (params?: { limit?: number; offset?: number }): Promise<OutputSchemaListItemsResponse> => {
    const response = await baseApiClient.get('/v1/output-schemas', { params });
    return response.data;
  },

  /**
   * Get schemas available to a specific agent
   */
  getForAgent: async (agentType: string): Promise<OutputSchemaListItemsResponse> => {
    const response = await baseApiClient.get(`/v1/output-schemas/for-agent/${agentType}`);
    return response.data;
  },

  /**
   * Get a single schema by key
   */
  get: async (key: string): Promise<OutputSchema> => {
    const response = await baseApiClient.get(`/v1/output-schemas/${key}`);
    return response.data;
  },
};
